import {
  FloatingDelayGroup,
  FloatingPortal,
  Placement,
  autoUpdate,
  flip,
  offset,
  safePolygon,
  useClick,
  useDelayGroup,
  useDismiss,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
  useRole,
} from "@floating-ui/react"
import {
  PropsWithChildren,
  ReactNode,
  cloneElement,
  isValidElement,
  useMemo,
  useState,
} from "react"

import { classNames } from "../../../../lib/classNames"

export interface FloatingTooltipProps {
  trigger: ReactNode
  content: ReactNode
  placement?: Placement
  className?: string
  offsetDistance?: number
  isDisabled?: boolean
  // Opens on focus, tap and Escape too; requires a focusable trigger.
  isInteractive?: boolean
  dismissOnTriggerClick?: boolean
  openDelay?: number
  variant?: "simple" | "complex"
  portal?:
    | { renderTo: "inline" }
    | { renderTo: "document" }
    | { renderTo: "custom"; root: HTMLElement }
}

export function FloatingTooltipGroup({ children }: PropsWithChildren) {
  return <FloatingDelayGroup delay={0}>{children}</FloatingDelayGroup>
}

export function FloatingTooltip({
  trigger,
  content,
  placement = "bottom-end",
  className = "",
  offsetDistance = 4,
  isDisabled = false,
  isInteractive = false,
  dismissOnTriggerClick = false,
  openDelay = 0,
  variant = "simple",
  portal = { renderTo: "inline" },
}: FloatingTooltipProps) {
  const [isOpen, setIsOpen] = useState(false)

  const { refs, floatingStyles, context } = useFloating({
    placement,
    open: isOpen,
    onOpenChange: setIsOpen,
    middleware: [flip(), offset(offsetDistance)],
    whileElementsMounted: autoUpdate,
  })

  const hover = useHover(context, {
    delay: { open: openDelay },
    handleClose: safePolygon(),
    move: !dismissOnTriggerClick,
  })
  const focus = useFocus(context, { enabled: isInteractive })
  const click = useClick(context, {
    enabled: isInteractive && !dismissOnTriggerClick,
    ignoreMouse: true,
  })
  const dismiss = useDismiss(context, {
    enabled: isInteractive,
    referencePress: dismissOnTriggerClick,
    referencePressEvent: "click",
  })
  const role = useRole(context, { role: "tooltip", enabled: isInteractive })
  useDelayGroup(context, { enabled: isInteractive })
  const { getReferenceProps, getFloatingProps } = useInteractions([
    hover,
    focus,
    click,
    dismiss,
    role,
  ])

  const portalRoot = useMemo(() => {
    if (portal.renderTo === "custom") return portal.root
    if (portal.renderTo === "document") return document.body
    return undefined
  }, [portal])

  if (isDisabled) return trigger

  const tooltip = (
    <div
      ref={refs.setFloating}
      style={floatingStyles}
      className={classNames(
        variant === "simple"
          ? "bg-neutral-bold-default color-inverse"
          : "bg-elevation-surface-default color-default",
        "shadow-surface rounded-overlay z-20 whitespace-pre-line px-3 py-2",
        className
      )}
      data-testid="floating-tooltip-content"
      {...getFloatingProps()}
    >
      {content}
    </div>
  )

  return (
    <>
      {isInteractive && isValidElement<Record<string, unknown>>(trigger) ? (
        // On the trigger itself, so aria-describedby lands where focus does.
        cloneElement(trigger, {
          ...getReferenceProps(trigger.props),
          ref: refs.setReference,
          "data-testid": trigger.props["data-testid"] ?? "floating-tooltip",
        })
      ) : (
        <div
          ref={refs.setReference}
          {...getReferenceProps()}
          data-testid="floating-tooltip"
        >
          {trigger}
        </div>
      )}
      {isOpen &&
        (portalRoot ? (
          <FloatingPortal root={portalRoot}>{tooltip}</FloatingPortal>
        ) : (
          tooltip
        ))}
    </>
  )
}
